import { Coins, Scale, TrendingUp } from "lucide-react";
import { Suspense, lazy } from "react";
import { defineFeature } from "../types";

/**
 * Currencies & FX — roadmap item 17 (PRD US-17.1 … US-17.7).
 *
 * /fx holds the finance views (realised and unrealised gain/loss, the
 * month-end revaluation, per-currency totals, the export-realisation tracker);
 * Settings → Currencies & FX holds the enabled currencies, today's rates,
 * manual rates and the base-currency lock. The invoice page and the dashboard
 * show rates through `@/components/fx`, not through this feature.
 */

const FxSettingsPage = lazy(() => import("./settings"));

/** Plain markup, not <Skeleton>: the registry imports this module statically. */
function SettingsFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading currency settings">
      <div className="skeleton-shimmer h-6 w-56 rounded-item" />
      <div className="skeleton-shimmer h-40 w-full rounded-card" />
      <div className="skeleton-shimmer h-64 w-full rounded-card" />
    </div>
  );
}

export default defineFeature({
  key: "fx",
  title: "Currencies & FX",
  routes: [{ path: "fx", lazy: () => import("./page") }],
  nav: [
    {
      label: "Currencies & FX",
      to: "/fx",
      icon: Coins,
      group: "finance",
      order: 60,
      permission: ["invoices:read", "financials:view"],
      description: "Exchange gains and losses, and export realisation",
    },
  ],
  commands: [
    { key: "fx.realised", label: "FX gain and loss", icon: TrendingUp, to: "/fx", group: "navigate", permission: ["invoices:read", "financials:view"], keywords: ["exchange", "forex", "currency", "usd"] },
    { key: "fx.revaluation", label: "Month-end FX revaluation", icon: Scale, to: "/fx?tab=revaluation", group: "actions", permission: ["invoices:read", "financials:view"], keywords: ["unrealised", "ind as 21"] },
    { key: "fx.rates", label: "Exchange rates", icon: Coins, to: "/settings/currencies", group: "navigate", permission: ["org:manage", "financials:view"], permissionMode: "any", keywords: ["fx", "rate", "override"] },
  ],
  settingsSections: [
    {
      key: "currencies",
      label: "Currencies & FX",
      path: "currencies",
      group: "Finance",
      order: 310,
      icon: Coins,
      permission: ["org:manage", "financials:view"],
      permissionMode: "any",
      description: "Currencies you bill in, today's rates, manual rates and the base-currency lock",
      element: (
        <Suspense fallback={<SettingsFallback />}>
          <FxSettingsPage />
        </Suspense>
      ),
    },
  ],
});
